import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Zap, MessageSquareWarning } from 'lucide-react';
import { apiGet } from '../../lib/api.js';

/** Badge copy for one active enrollment: "Active Drip: Name - Step 2 Sent" (or "Queued" before the first send). */
export function activeDripLabel({ name, stepsSent }) {
  return `Active Drip: ${name} - ${stepsSent > 0 ? `Step ${stepsSent} Sent` : 'Queued'}`;
}

/**
 * Shows the lead's active drip(s), or a "Replied - Manual Action Needed" notice when a
 * lead's text stopped a drip. Renders nothing when there is neither.
 * @param {{leadId: string}} props
 */
export default function ActiveDripBadge({ leadId }) {
  const { data } = useQuery({
    queryKey: ['lead-active-drip', leadId],
    queryFn: () => apiGet(`/drips/active/${leadId}`).catch(() => null),
    enabled: !!leadId,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const active = data?.active || [];
  const replied = data?.replied;
  if (!active.length && !replied) return null;

  return (
    <div className="mt-2 space-y-1" data-testid="active-drip-badge">
      {active.map((d) => (
        <div key={d.enrollmentId} className="flex items-center gap-1.5 rounded-md border border-civix-purple/40 bg-civix-purple/15 px-2 py-1 text-[11px] font-medium text-civix-purpleSoft">
          <Zap className="h-3 w-3 shrink-0" />
          <span className="truncate">{activeDripLabel(d)}</span>
        </div>
      ))}
      {replied && (
        <div className="flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] font-medium text-amber-300">
          <MessageSquareWarning className="h-3 w-3 shrink-0" />
          <span className="truncate">Replied - Manual Action Needed ({replied.name})</span>
        </div>
      )}
    </div>
  );
}
